import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Translate, withLocalize } from "react-localize-redux";
import { useOutletContext, useParams } from "react-router";
import Navigation from "../../../utils/components/Navigation";
import { BsEyeFill, BsPlusCircle } from "react-icons/bs";
import { AiOutlineSearch } from "react-icons/ai";
import DataTable from "../../../utils/components/DataTable";
import getDefaultAvatar from "../../../utils/helpers/getDefaultAvatar";
import { RiPencilFill } from "react-icons/ri";
import { MdLockOpen } from "react-icons/md";
import NewCompanyUserModal from "./components/NewCompanyUserModal";
import UserViewModal from "./components/UserViewModal";
import Loading from "../../../utils/components/Loading";
import { useProject } from "../../../utils/hooks/useProject";
import API from "../../../utils/API";
import toastr from "toastr";
import { useCan } from "../../../utils/hooks/useCan";

const USER_STATUSES = ["active", "passive"];

const SEARCH_TIMEOUT = 800;

const CompanyUsers = ({ translate }) => {
  const {
    setBreadcrumbs,
    users,
    groups,
    company,
    totalUsers,
    usersLoading,
    getUsers,
  } = useOutletContext();
  const [isLoading, setIsLoading] = useState(true);
  const [userViewInfo, setUserViewInfo] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [isModalOn, setIsModalOn] = useState(false);
  const [isUserViewModalOn, setIsUserViewModalOn] = useState(false);

  const { id } = useParams();
  const { project } = useProject();

  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [searchedQuery, setSearchedQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const [userRoles, setUserRoles] = useState([]);

  const [sortQuery, setSortQuery] = useState({
    sortBy: "user_name",
    orderBy: "asc",
  });

  const can = useCan();

  const columns = [
    {
      name: "#",
      selector: (row) => row.avatar,
      center: true,
      compact: true,
      maxWidth: "90px",
      cell: (row) =>
        row.avatar ? (
          <div className="avatar rounded-circle d-flex justify-content-center align-items-center overflow-hidden">
            <img
              src={row.avatar}
              alt={row.name}
              className="w-100 h-100 rounded-circle"
            />
          </div>
        ) : (
          <div className="default-avatar">{getDefaultAvatar(row.name)}</div>
        ),
    },
    {
      name: <Translate id="company.users.headers.user-name"/>,
      selector: (row) => row.name,
      sortable: true,
      left: true,
      sortField: "user_name",
    },
    {
      name: <Translate id="company.users.headers.user-role"/>,
      selector: (row) => row.application_role.display_name,
      sortable: true,
      center: true,
      cell: (row) => (
        <div className={`role role--${row.application_role.id}`}>
          {row.application_role.display_name}
        </div>
      ),
      sortField: "application_role",
    },
    {
      name: <Translate id="company.users.headers.user-status"/>,
      selector: (row) => row.status,
      center: true,
      cell: (row) => (
        <div className={`status status--${row.status}`}>
          {row.status
            ? USER_STATUSES[0].toLocaleUpperCase()
            : USER_STATUSES[1].toLocaleUpperCase()}
        </div>
      ),
    },
    {
      name: <Translate id="company.users.headers.operations"/>,
      center: true,
      cell: (row) => (
        <div className="actions-holder d-flex align-items-center justify-content-between">
          <div
            role="button"
            className="action-item"
            onClick={() => {
              setSelectedUserId(row.id);
              handleClickUserView(row);
            }}
          >
            <BsEyeFill/>
          </div>
          {can.doThat("user:update", {
            company_id: id,
            project_id: company?.projects?.find((p) => p?.id === project?.id)
              ?.id,
          }) && (
            <div
              role="button"
              onClick={() => {
                setSelectedUserId(row.id);
                setIsModalOn(true);
              }}
              className="action-item"
            >
              <RiPencilFill/>
            </div>
          )}
          {can.doThat("user:update", {
            company_id: id,
            project_id: company?.projects?.find((p) => p?.id === project?.id)
              ?.id,
          }) && (
            <div
              role="button"
              className="action-item"
              onClick={() => handleActivityStatus(row)}
            >
              <MdLockOpen/>
            </div>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    setTotalRows(totalUsers);
  }, [totalUsers]);

  const getUserById = (id) => {
    let url = "user/" + id;
    return API.getData(url).then(({ data: res }) => {
      return res.data;
    });
  };

  const handleActivityStatus = async (row) => {
    const updatedUser = {
      status: !row.status,
    };

    setIsLoading(true);

    API.updateUserData(updatedUser, row.id).then(({ data: res }) => {
      toastr.success(
        translate("company.users.messages.user-status-update-success"),
      );
      handleGetCompanyUsers();
    });
  };

  const handleClickUserView = async (row) => {
    const currentUserData = await getUserById(row.id);

    setUserViewInfo({
      Name: currentUserData.name,
      Title: currentUserData.title,
      Email: currentUserData.email,
      Company: company.name,
    });

    setIsUserViewModalOn(true);
  };

  useEffect(() => {
    if (!users) return;
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  useEffect(() => {
    setBreadcrumbs([
      {
        id: 0,
        title: "page_titles.companies",
        route: "auth.companies",
      },
      { id: 1, customTitle: company?.name || "" },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    if (!isModalOn) {
      setSelectedUserId(null);
    }
  }, [isModalOn]);

  const handleSearch = (e) => {
    const query = e.target.value;

    setIsLoading(true);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (query === searchedQuery || !query) {
      setIsLoading(false);
      setQuery(query);
      return;
    }

    setSearchTimeout(
      setTimeout(() => {
        setPage(1);
        setQuery(query);
      }, SEARCH_TIMEOUT),
    );
  };

  const handleGetCompanyUsers = (
    sortBy = sortQuery?.sortBy,
    orderBy = sortQuery?.orderBy,
  ) => {
    if (sortBy && orderBy) {
      setSortQuery({ sortBy, orderBy });
    }

    getUsers({
      sortBy,
      orderBy,
      page,
      rowsPerPage,
      query,
    });

    setSearchedQuery(query);
  };

  useEffect(() => {
    handleGetCompanyUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, page, rowsPerPage]);

  const handleSort = (column, orderBy) => {
    const { sortField: sortBy } = column;

    if (!sortBy || !orderBy) return;

    setSortQuery({ sortBy, orderBy: orderBy });
    getUsers({ sortBy, orderBy, page, rowsPerPage, query });
  };

  return (
    <div className="CompanyDetailPage CompanyUsers">
      <Helmet>
        <title>{translate("page_titles.company-users")}</title>
      </Helmet>
      <header>
        <Navigation
          title={translate("company.users.title")}
          inputPlaceholder={translate("company.filter_placeholder")}
          inputOnChange={handleSearch}
          iconNode={<AiOutlineSearch className="fs-5 me-2 mt-2"/>}
          buttonNode={
            project?.id &&
            can.doThat("user:create", {
              company_id: id,
              project_id: company?.projects?.find((p) => p?.id === project?.id)
                ?.id,
            }) ? (
              <p className="m-0 p-0 text-center">
                <BsPlusCircle className="fs-4 me-2"/>
                {translate("company.users.new_user")}
              </p>
            ) : (
              false
            )
          }
          onButtonClick={() => {
            setSelectedUserId(null);
            setIsModalOn(true);
          }}
        />
      </header>

      <DataTable
        pagination
        data={users}
        columns={columns}
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        onChangeRowsPerPage={(rowsPerPage) => {
          setRowsPerPage(rowsPerPage);
          setResetPaginationToggle(!resetPaginationToggle);
        }}
        onChangePage={(page) => {
          setPage(page);
          setResetPaginationToggle(!resetPaginationToggle);
        }}
        progressPending={isLoading || usersLoading}
        progressComponent={
          <div className="py-3">
            <Loading classNames={"my-3"}/>
          </div>
        }
        noDataComponent={
          <div className="text-center py-3">
            {translate("company.users.no_users")}
          </div>
        }
        onSort={handleSort}
        sortServer
        // paginationResetDefaultPage={resetPaginationToggle}
      />

      {can.doThat("user:create", {
        company_id: id,
        project_id: company?.projects?.find((p) => p?.id === project?.id)?.id,
      }) && (
        <NewCompanyUserModal
          onClose={() => {
            setIsModalOn(false);
          }}
          selectedUserId={selectedUserId}
          companyId={id}
          projectId={project?.id}
          groups={groups}
          show={isModalOn}
          refreshData={handleGetCompanyUsers}
          translate={translate}
          userRoles={userRoles}
          setUserRoles={setUserRoles}
          companyProjects={company?.projects}
          page={page}
        />
      )}
      <UserViewModal
        show={isUserViewModalOn}
        hideModal={() => setIsUserViewModalOn(false)}
        userInfo={userViewInfo}
      />
    </div>
  );
};

export default withLocalize(CompanyUsers);
